import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Grid,
  Paper,
  Divider,
  FormControl,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import {
  Person,
  Email,
  Subject,
  Message,
  AccessTime,
  Update
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const ContactDetailDialog = ({ open, contact, onClose, onStatusChange }) => {
  const { t } = useTranslation();

  if (!contact) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return 'error';
      case 'read':
        return 'warning';
      case 'responded':
        return 'success';
      case 'archived':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'new':
        return t('contact.status.new');
      case 'read':
        return t('contact.status.read');
      case 'responded':
        return t('contact.status.responded');
      case 'archived':
        return t('contact.status.archived');
      default:
        return status;
    }
  };

  const handleStatusChange = (newStatus) => {
    onStatusChange(contact._id, newStatus);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {t('contact.contactDetails')}
          </Typography>
          <Chip
            label={getStatusText(contact.status)}
            color={getStatusColor(contact.status)}
            size="small"
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* Contact Information */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person />
                {t('contact.contactInformation')}
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      {t('contact.firstName')}:
                    </Typography>
                    <Typography variant="body1">
                      {contact.firstName}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      {t('contact.lastName')}:
                    </Typography>
                    <Typography variant="body1">
                      {contact.lastName}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Email fontSize="small" />
                    <Typography variant="body2" color="textSecondary">
                      {t('contact.email')}:
                    </Typography>
                    <Typography variant="body1">
                      {contact.email}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Message Content */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Message />
                {t('contact.messageContent')}
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Subject fontSize="small" />
                  <Typography variant="body2" color="textSecondary">
                    {t('contact.subject')}:
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                  {contact.subject}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {t('contact.message')}:
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {contact.message}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Timestamps and Status */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTime />
                {t('contact.timestamps')}
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      {t('contact.createdAt')}:
                    </Typography>
                    <Typography variant="body1">
                      {new Date(contact.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      {t('contact.updatedAt')}:
                    </Typography>
                    <Typography variant="body1">
                      {new Date(contact.updatedAt).toLocaleString()}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Update fontSize="small" />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>{t('contact.status.label')}</InputLabel>
                  <Select
                    value={contact.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    label={t('contact.status.label')}
                  >
                    <MenuItem value="new">{t('contact.status.new')}</MenuItem>
                    <MenuItem value="read">{t('contact.status.read')}</MenuItem>
                    <MenuItem value="responded">{t('contact.status.responded')}</MenuItem>
                    <MenuItem value="archived">{t('contact.status.archived')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {t('common.close')}
        </Button>
        <Button
          variant="contained"
          onClick={() => window.open(`mailto:${contact.email}?subject=Re: ${contact.subject}`)}
        >
          {t('contact.replyByEmail')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactDetailDialog;